import { RotateCcw, Info } from 'lucide-react';
import { useMapSettings } from '@app/contexts/MapSettingsContext';
import { useTheme } from '@app/theme/ThemeContext';
import SettingsPanel from './SettingsPanel';

interface MapSettingsPanelProps {
  onClose: () => void;
  openedFromLayers?: boolean;
  onBack?: () => void;
}

export default function MapSettingsPanel({
  onClose,
  openedFromLayers = false,
  onBack,
}: MapSettingsPanelProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { settings, updateSettings, resetToDefaults } = useMapSettings();

  const handleSettingChange = (newSettings: typeof settings) => {
    updateSettings(newSettings);
  };

  const handleReset = () => {
    resetToDefaults();
  };

  const handleClose = () => {
    if (openedFromLayers && onBack) {
      onBack();
    } else {
      onClose();
    }
  };

  const footerActions = (
    <div className="flex justify-start">
      <button
        onClick={handleReset}
        className={`flex items-center gap-2 px-3 py-1 text-xs rounded-md transition-colors ${
          isDark
            ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-gray-100'
            : 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900'
        }`}
      >
        <RotateCcw size={14} />
        Reset to Defaults
      </button>
    </div>
  );

  return (
    <SettingsPanel
      isDark={isDark}
      title="Map Performance Settings"
      onClose={handleClose}
      actions={footerActions}
      className="max-w-md"
      showBackButton={openedFromLayers}
    >
      {/* Prefetch Window Size */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Prefetch Window Size
          </label>
          <div className="group relative">
            <Info
              size={14}
              className={`${isDark ? 'text-gray-400' : 'text-gray-500'} cursor-help`}
            />
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-10">
              How many future frames to preload for smooth animation
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min="1"
            max="100"
            value={settings.prefetchWindowSize}
            onChange={(e) =>
              handleSettingChange({
                ...settings,
                prefetchWindowSize: Number(e.target.value),
              })
            }
            className="flex-1"
            style={{
              accentColor: '#E34290',
            }}
          />
          <div
            className={`min-w-[3rem] text-right text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
          >
            {settings.prefetchWindowSize}
          </div>
        </div>
        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Higher values = smoother animation, but more memory usage
        </p>
      </div>

      {/* Animation Speed */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Animation Speed (FPS)
          </label>
          <div className="group relative">
            <Info
              size={14}
              className={`${isDark ? 'text-gray-400' : 'text-gray-500'} cursor-help`}
            />
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-10">
              Animation frames per second (higher = faster)
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min="1"
            max="60"
            step="1"
            value={settings.animationSpeed}
            onChange={(e) =>
              handleSettingChange({
                ...settings,
                animationSpeed: Number(e.target.value),
              })
            }
            className="flex-1"
            style={{
              accentColor: '#E34290',
            }}
          />
          <div
            className={`min-w-[3rem] text-right text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
          >
            {settings.animationSpeed} FPS
          </div>
        </div>
        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Frame delay: {Math.round(1000 / settings.animationSpeed)}ms
        </p>
      </div>
    </SettingsPanel>
  );
}
